import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Phone } from "lucide-react";
import logoAsset from "@/assets/logo.svg.asset.json";
import heroBackground from "@/assets/hero-bg.webp";

interface MaintenanceProps {
  message?: string | null;
}

const Maintenance = ({ message }: MaintenanceProps) => {
  const { data: contact } = useQuery({
    queryKey: ["contact-info"],
    queryFn: async () => {
      const { data } = await supabase.from("contact_info").select("*").limit(1).maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    document.title = "Web v úpravě — Umělecké Tesařství";
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      if (prev) meta!.setAttribute("content", prev);
      else meta!.remove();
    };
  }, []);

  const text =
    message?.trim() ||
    "Pracujeme na vylepšeních. Brzy jsme zpět.";

  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-[#17110d] text-white">
      <img
        src={heroBackground}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 -z-20 h-full w-full object-cover opacity-35"
      />
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(110deg,rgba(20,13,9,0.96)_0%,rgba(31,20,13,0.88)_48%,rgba(20,13,9,0.7)_100%)]" />
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-[12%] -z-10 hidden w-px bg-white/10 md:block"
      />

      <main className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-6 py-8 sm:px-10 sm:py-10 lg:px-16">
        <header className="flex items-center justify-between border-b border-white/15 pb-6">
          <img
            src={logoAsset.url}
            alt="Umělecké Tesařství"
            className="h-12 w-auto brightness-0 invert sm:h-14"
          />
          <span className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-white/55 sm:block">
            Tradiční řemeslo · Praha
          </span>
        </header>

        <section className="flex flex-1 items-center py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="mb-7 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#d8ad72]">
              <span className="h-px w-10 bg-[#d8ad72]" />
              Dočasně pozastaveno
            </div>

            <h1 className="max-w-3xl font-serif text-5xl font-semibold leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl">
              Web je momentálně v úpravě
            </h1>

            <p className="mt-7 max-w-2xl whitespace-pre-line text-lg leading-relaxed text-white/72 sm:text-xl">
              {text}
            </p>

            {(contact?.email || contact?.phone) && (
              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                {contact?.phone && (
                  <a
                    href={`tel:${contact.phone.replace(/\s+/g, "")}`}
                    className="inline-flex min-h-12 items-center justify-center gap-2 border border-[#d8ad72] bg-[#d8ad72] px-6 text-sm font-semibold text-[#21150e] transition-colors hover:bg-[#e5bf8a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#17110d]"
                  >
                    <Phone className="h-4 w-4" />
                    {contact.phone}
                  </a>
                )}
                {contact?.email && (
                  <a
                    href={`mailto:${contact.email}`}
                    className="inline-flex min-h-12 items-center justify-center gap-2 border border-white/25 bg-white/5 px-6 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#17110d]"
                  >
                    <Mail className="h-4 w-4" />
                    {contact.email}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>

        <footer className="flex flex-col gap-2 border-t border-white/15 pt-6 text-xs text-white/45 sm:flex-row sm:items-center sm:justify-between">
          <span>Umělecké tesařství s.r.o.</span>
          <span>Děkujeme za trpělivost.</span>
        </footer>
      </main>
    </div>
  );
};

export default Maintenance;
